import moment from 'moment';
import axios, { formToJSON } from 'axios';
import _, { add } from 'lodash';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const toDateOnly = (value: Date | string): string => {
  if (typeof value === 'string' && DATE_ONLY_RE.test(value)) return value;
  const d = value instanceof Date ? value : new Date(value);
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseDateOnly = (value: string | Date): Date => {
  if (value instanceof Date) return value;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

export interface Addon {
  lookupCode: string;
  count: number;
}

export interface Occupancy {
  from: string;
  to: string;
}

export interface Day {
  day: number;
  overnightRoomsEZ: number;
  overnightRoomsDZ: number;
  totalGuests: number;
  occupancy: Occupancy[];
  seating: string[];
  seatCount: number[];
  addons: Addon[];
  serviceTypes: ServiceTypeConfiguration[];
  seatCountsManuallyEdited?: boolean;
  regularGuestsSynced?: boolean;
}

export interface AddonProduct {
  visible: boolean;
  checked: boolean;
  sku: string;
  name: string;
  header: string;
  details: string;
  isAddon: boolean;
  isDeduction: boolean;
  lookupCode: string;
  recurring: string;
  count: number;
}

export interface ServiceTypeConfiguration {
  type: 'SMALL' | 'MEDIUM' | 'REGULAR';
  guestsCount: number;
}

export interface IFormState {
  start: string;
  end: string;
  duration: string;
  meetingRooms: number;
  prevdayRoomsEZ: number;
  prevdayRoomsDZ: number;
  prevdayServiceTypes: ServiceTypeConfiguration[];
  rooms: number;
  price: string;
  days: Day[];
  prevdayAddons: Addon[];
  service: any;
  placeName: string;
  placeLat: number;
  placeLng: number;
  // Pre-computed bounds for a curated Seminargo region/überregion selection.
  // When set, the platform fits the map to these bounds directly (no Google geocode).
  placeBounds?: { north: number; south: number; east: number; west: number } | null;
  searchedHotelId: number | null;
  bookingRefCode: string | null;
  bookingHotelRefCodes: string[];
  bookingHotelStatus: string[];
  hotelMessage?: string; 
}

const initialFormState: IFormState = {
  start: moment().add(1, 'days').format('YYYY-MM-DD'),
  end: moment().add(2, 'days').format('YYYY-MM-DD'),
  duration: 'Ganztag',
  meetingRooms: 1,
  days: [
    {
      day: 0,
      overnightRoomsEZ: 0,
      overnightRoomsDZ: 0,
      totalGuests: 10,
      occupancy: [
        {
          from: '08:00',
          to: '18:00',
        },
      ],
      seating: ['UFORM'],
      seatCount: [10],
      addons: [],
      serviceTypes: [
        {
          type: 'REGULAR',
          guestsCount: 10,
        },
      ],
      seatCountsManuallyEdited: false,
      regularGuestsSynced: true,
    },
    {
      day: 1,
      overnightRoomsEZ: 0,
      overnightRoomsDZ: 0,
      totalGuests: 10,
      occupancy: [
        {
          from: '08:00',
          to: '18:00',
        },
      ],
      seating: ['UFORM'],
      seatCount: [10],
      addons: [],
      serviceTypes: [
        {
          type: 'REGULAR',
          guestsCount: 10,
        },
      ],
      seatCountsManuallyEdited: false,
      regularGuestsSynced: true,
    },
  ],
  rooms: 0,
  prevdayRoomsEZ: 0,
  prevdayRoomsDZ: 0,
  prevdayServiceTypes: [
    {
      type: 'REGULAR',
      guestsCount: 0,
    },
  ],
  prevdayAddons: [],
  placeName: typeof window !== 'undefined' ? new URLSearchParams(window?.location.search).get('place') || 'Wien, Österreich' : 'Wien, Österreich',
  placeLat: 48.20849,
  placeLng: 16.37208,
  placeBounds: null,
  price: '0',
  service: {
    sku: '',
  },
  searchedHotelId: null,
  bookingRefCode: null,
  bookingHotelRefCodes: [],
  bookingHotelStatus: [],
};

export const getUserLocation = async (): Promise<{ latitude: number; longitude: number; placeName: string }> => {
  return new Promise((resolve, reject) => {
    if (navigator?.geolocation) {
      navigator.geolocation.getCurrentPosition(
        function (position) {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          axios
            .get(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&sensor=true&key=AIzaSyBQUQ-CRUNnxBECaV4OuoYcpC9eWTlNBr0`,
            )
            .then(res => {
              const place = res.data.results[0].formatted_address;
              resolve({ latitude, longitude, placeName: place });
            })
            .catch(e => {
              reject(e);
            });
        },
        e => {
          reject(e);
        },
      );
    } else {
      reject('Geolocation is not supported by this browser.');
    }
  });
};

const DEFAULT_PLACE = {
  placeName: 'Wien, Österreich',
  placeLat: 48.20849,
  placeLng: 16.37208,
  placeBounds: null as IFormState['placeBounds'],
  searchedHotelId: null as number | null,
};

const getPricelistGraphqlUrl = (baseUrl: string): string => {
  const trimmed = (baseUrl || '').trim();
  if (!trimmed) return '/pricelist/graphql';

  const noTrailingSlash = trimmed.replace(/\/+$/, '');

  if (noTrailingSlash.endsWith('/pricelist/graphql')) return noTrailingSlash;
  if (noTrailingSlash.endsWith('/pricelist')) return `${noTrailingSlash}/graphql`;

  return `${noTrailingSlash}/pricelist/graphql`;
};

const getUrlSearchParams = (): URLSearchParams | null => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search);
};

/** Full widget state handoff via ?s= takes priority over ?placeName=. */
const hasFullStateUrlParam = (): boolean => !!getUrlSearchParams()?.get('s');

/** Marketing deep-link place preset, e.g. ?placeName=Burgenland */
export const getPlaceNameUrlParam = (): string | null => {
  if (hasFullStateUrlParam()) return null;
  const value = getUrlSearchParams()?.get('placeName')?.trim();
  return value || null;
};

type ResolvedPlace = Pick<IFormState, 'placeName' | 'placeLat' | 'placeLng' | 'placeBounds' | 'searchedHotelId'>;

interface RegionSearchHit {
  name: string;
  north: number;
  south: number;
  east: number;
  west: number;
}

const regionToResolvedPlace = (region: RegionSearchHit): ResolvedPlace => ({
  placeName: region.name,
  placeLat: (region.north + region.south) / 2,
  placeLng: (region.east + region.west) / 2,
  placeBounds: {
    north: region.north,
    south: region.south,
    east: region.east,
    west: region.west,
  },
  searchedHotelId: null,
});

/** Curated Seminargo regions, same source as the Ort autocomplete dropdown. */
const searchCuratedRegions = async (searchTerm: string, pricelistServiceUrl: string): Promise<RegionSearchHit[]> => {
  try {
    const response = await fetch(getPricelistGraphqlUrl(pricelistServiceUrl), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          query SearchRegions($searchTerm: String!) {
            searchRegions(searchTerm: $searchTerm, limit: 5) {
              name
              north
              south
              east
              west
            }
          }
        `,
        variables: { searchTerm },
      }),
    });
    const result = await response.json();
    return result?.data?.searchRegions || [];
  } catch (error) {
    console.error('Error resolving placeName via searchRegions:', error);
    return [];
  }
};

const geocodePlaceName = async (searchTerm: string): Promise<ResolvedPlace | null> => {
  try {
    const geoResponse = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(searchTerm)}&key=AIzaSyBQUQ-CRUNnxBECaV4OuoYcpC9eWTlNBr0`,
    );
    const geoResult = geoResponse.data?.results?.[0];
    if (geoResult?.geometry?.location) {
      const viewport = geoResult.geometry.viewport;
      return {
        placeName: geoResult.formatted_address || searchTerm,
        placeLat: geoResult.geometry.location.lat,
        placeLng: geoResult.geometry.location.lng,
        placeBounds: viewport
          ? {
              north: viewport.northeast.lat,
              south: viewport.southwest.lat,
              east: viewport.northeast.lng,
              west: viewport.southwest.lng,
            }
          : null,
        searchedHotelId: null,
      };
    }
  } catch (error) {
    console.error('Error resolving placeName via geocode:', error);
  }
  return null;
};

/**
 * Resolves a marketing placeName to a map location.
 * @param pricelistServiceUrl - Base URL of the pricelist GraphQL API used for region search
 * @returns Resolved place, or null when neither region search nor geocoding matches
 */
const resolvePlaceName = async (placeName: string, pricelistServiceUrl: string): Promise<ResolvedPlace | null> => {
  const searchTerm = placeName.trim();
  if (searchTerm.length < 2) return null;

  const regions = await searchCuratedRegions(searchTerm, pricelistServiceUrl);

  // A curated region named like the input wins. A Bundesland only exists as an attribute of its
  // sub-regions, so region search answers "Burgenland" with sub-regions - geocoding fits those better.
  const namedRegion = regions.find(region => region.name.trim().toLowerCase() === searchTerm.toLowerCase());
  if (namedRegion) return regionToResolvedPlace(namedRegion);

  const geocoded = await geocodePlaceName(searchTerm);
  if (geocoded) return geocoded;

  // Ticket rule: with several possible locations and no better signal, take the first.
  return regions.length > 0 ? regionToResolvedPlace(regions[0]) : null;
};

let pendingPlaceName: { key: string; promise: Promise<ResolvedPlace | null> } | null = null;

/**
 * Shares one lookup between all consumers of the same preset - every useFormState instance
 * resolves on mount, and region search plus geocoding are both billed per request.
 */
const resolvePlaceNameOnce = (placeName: string, pricelistServiceUrl: string): Promise<ResolvedPlace | null> => {
  const key = `${placeName.trim().toLowerCase()}|${pricelistServiceUrl}`;
  if (pendingPlaceName?.key !== key) {
    pendingPlaceName = {
      key,
      promise: resolvePlaceName(placeName, pricelistServiceUrl).then(resolved => {
        // Let a later mount retry instead of pinning the whole session to the fallback place.
        if (!resolved && pendingPlaceName?.key === key) pendingPlaceName = null;
        return resolved;
      }),
    };
  }
  return pendingPlaceName.promise;
};

const getPreviousState = () => {
  try {
    const urlParams = new URLSearchParams(window?.location.search);
    if (urlParams.get('s')) {
      const state = JSON.parse(urlParams.get('s') as any);
      state.start = toDateOnly(state.start);
      state.end = toDateOnly(state.end);
      return state;
    } else {
      console.log('No state in URL');
      throw new Error('No state in URL');
    }
  } catch (e) {
    try {
      if (localStorage.getItem('formState')) {
        const s = JSON.parse(localStorage.getItem('formState') as any);
        if (s !== null && s.start && s.end) {
          console.log('State in local storage');
          s.start = toDateOnly(s.start);
          s.end = toDateOnly(s.end);
          return s;
        } else {
          console.log('No state in local storage');
          throw new Error('No state in local storage');
        }
      }
    } catch (e) {
      return null;
    }
  }
  return null;
};

const mergePreviousFormState = (prevState: IFormState): IFormState => {
  // Fallback to initialFormState.days if prevState.days is undefined/null (backward compatibility)
  const days = prevState.days || initialFormState.days;
  return {
    ...initialFormState,
    ..._.omit(prevState, ['prevdayGuestsEZ', 'prevdayGuestsDZ']),
    // Ensure prevdayServiceTypes is always an array (for backward compatibility with old saved states)
    prevdayServiceTypes: prevState.prevdayServiceTypes || initialFormState.prevdayServiceTypes,
    days: days.map(day => ({
      ...initialFormState.days[0],
      ..._.omit(day, ['overnightGuestsEZ', 'overnightGuestsDZ']),
      // Ensure serviceTypes is always an array for each day
      serviceTypes: day.serviceTypes || initialFormState.days[0].serviceTypes,
      // Ensure occupancy is always an array for each day (backward compatibility)
      occupancy: (day.occupancy || initialFormState.days[0].occupancy).map(o => ({
        from: typeof o === 'string' ? '08:00' : o.from,
        to: typeof o === 'string' ? '18:00' : o.to,
      })),
    })),
  };
};

export const getInitialFormState = async (skipGeolocation = false): Promise<IFormState> => {
  /*try {
    const userLocation = await getUserLocation();
    console.log('Initital - User location: ' + userLocation.placeName);
    return {
      ...initialFormState,
      placeLat: userLocation.latitude,
      placeLng: userLocation.longitude,
      placeName: userLocation.placeName
    }
  } catch (e) {
    return initialFormState;
  }*/
  // Browser geolocation would race with and overwrite a ?placeName= preset.
  if (!skipGeolocation && !getPlaceNameUrlParam()) {
    getUserLocation()
      .then(res => {
        if (_.isNil(localStorage.getItem('formStateManual'))) {
          const state = {
            ...getPreviousState(),
            placeLat: res.latitude,
            placeLng: res.longitude,
            placeName: res.placeName,
          };
          updateFormState(state);
          //const event = new CustomEvent('formStateLocationChange', { detail: state });
          //window.dispatchEvent(event);
        }
      })
      .catch(e => {
        console.error(e);
      });
  }
  return initialFormState;
};

/**
 * Loads lister form state from ?s=, localStorage, or defaults.
 * A standalone ?placeName= presets Ort on top of that, falling back to Wien when nothing matches.
 * @param pricelistServiceUrl - Base URL of the pricelist GraphQL API used for region search
 */
export const getFormState = async (pricelistServiceUrl = ''): Promise<IFormState> => {
  const placeNameParam = getPlaceNameUrlParam();
  const prevState: IFormState = getPreviousState();

  let state: IFormState;
  if (prevState) {
    const mergedState = mergePreviousFormState(prevState);
    console.log('Previous state: ' + JSON.stringify(prevState));
    console.log('Merged state: ' + JSON.stringify(mergedState));
    state = mergedState;
  } else {
    state = await getInitialFormState(!!placeNameParam);
  }

  // ?s= is already applied by getPreviousState and outranks ?placeName=.
  if (placeNameParam) {
    const resolved = await resolvePlaceNameOnce(placeNameParam, pricelistServiceUrl);
    state = {
      ...state,
      ...(resolved || DEFAULT_PLACE),
    };
    // Marks the location as user-intent so async geolocation leaves the preset alone.
    localStorage.setItem('formStateManual', 'true');
    updateFormState(state);
  }

  return state;
};

/**
 * Resolves a place name to map coordinates (curated region first, then geocode).
 * Used when applying booking templates and marketing ?placeName= presets.
 */
export const resolveListerPlaceName = resolvePlaceNameOnce;

export const isInitalFormState = async (state: IFormState) => {
  const initialFormState = await getInitialFormState();
  console.log('Initial form state: ' + JSON.stringify(initialFormState));
  console.log('Current form state: ' + JSON.stringify(state));
  return JSON.stringify(state) === JSON.stringify(initialFormState);
};

export const updateFormState = (state: IFormState) => {
  state.start = toDateOnly(state.start);
  state.end = toDateOnly(state.end);
  localStorage.setItem('formState', JSON.stringify(state));
  const event = new CustomEvent('formStateChange', { detail: state });
  window.dispatchEvent(event);
};

const WIDGET_RESET_VALIDATED_AT_KEY = 'widgetResetValidatedAt';

export const clearFormStateIfReset = async (pricelistServiceUrl: string): Promise<boolean> => {
  try {
    const response = await fetch(getPricelistGraphqlUrl(pricelistServiceUrl), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `query { widgetResetTimestamp }`,
      }),
    });
    const result = await response.json();
    const serverTimestamp = result?.data?.widgetResetTimestamp;

    if (!serverTimestamp) {
      return false;
    }

    const serverTime = new Date(serverTimestamp).getTime();
    const localValidatedAt = localStorage.getItem(WIDGET_RESET_VALIDATED_AT_KEY);
    const localTime = localValidatedAt ? parseInt(localValidatedAt, 10) : 0;

    if (serverTime > localTime) {
      localStorage.removeItem('formState');
      localStorage.removeItem('formStateManual');
      localStorage.setItem(WIDGET_RESET_VALIDATED_AT_KEY, serverTime.toString());
      window.dispatchEvent(new CustomEvent('formStateReset', {}));
      return true;
    }
  } catch (error) {
    console.error('Error checking widget reset timestamp:', error);
  }
  return false;
};

export const handleWidgetFormStateReset = (timestamp: Date) => {
  const serverTime = new Date(timestamp).getTime();
  const localValidatedAt = localStorage.getItem(WIDGET_RESET_VALIDATED_AT_KEY);
  const localTime = localValidatedAt ? parseInt(localValidatedAt, 10) : 0;

  if (serverTime > localTime) {
    localStorage.removeItem('formState');
    localStorage.removeItem('formStateManual');
    localStorage.setItem(WIDGET_RESET_VALIDATED_AT_KEY, serverTime.toString());
    window.dispatchEvent(new CustomEvent('formStateReset', {}));
  }
};
